import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { MessageSquare, Send, Loader2, ChevronLeft, HelpCircle, Paperclip, History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 3000;
const MAX_BYTES = 5 * 1024 * 1024;

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Renders an attachment: image thumbnail (click to open) or a file chip link.
const AttachmentBlock = ({ attachment, mine }) => {
  if (!attachment?.url) return null;
  if (attachment.is_image) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block mt-1">
        <img
          src={attachment.url}
          alt={attachment.filename || "image"}
          className="max-h-52 max-w-full rounded-lg border border-[#1a3a6a] object-cover"
        />
      </a>
    );
  }
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className={`mt-1 inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border ${
        mine
          ? "bg-[#f4cc1e] text-[#091c3c] border-[#f4cc1e]"
          : "bg-[#0d2a5a] text-white border-[#1a3a6a]"
      }`}
    >
      <Paperclip size={13} /> {attachment.filename || "Download file"}
    </a>
  );
};

// One message row (customer perspective: "mine" = customer-sent).
const CustomerMessage = ({ m }) => {
  if (m.sender_role === "system") {
    return (
      <div className="flex justify-center">
        <div className="max-w-[92%] bg-[#0d2a5a]/60 border border-[#1a3a6a] text-white/80 text-sm rounded-lg px-4 py-3 whitespace-pre-wrap">
          {m.body}
        </div>
      </div>
    );
  }
  const mine = m.sender_role === "customer";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[78%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
        {m.body && (
          <div
            className={`px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${
              mine ? "bg-[#f4cc1e] text-[#091c3c]" : "bg-[#0d2a5a] text-white border border-[#1a3a6a]"
            }`}
          >
            {m.body}
          </div>
        )}
        {m.attachment && <AttachmentBlock attachment={m.attachment} mine={mine} />}
        <span className="text-[10px] text-white/40 mt-1 px-1">
          {mine ? "You" : m.sender_name} · {formatTime(m.created_at)}
        </span>
      </div>
    </div>
  );
};

const CustomerChat = () => {
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  // FAQ self-service pills
  const [faqs, setFaqs] = useState([]);
  const [faqCategory, setFaqCategory] = useState(null);
  const [faqThread, setFaqThread] = useState([]); // [{key, question, answer}]

  // Past (closed) conversations
  const [pastConvs, setPastConvs] = useState([]);
  const [viewConv, setViewConv] = useState(null);
  const [viewMessages, setViewMessages] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const lastSeqRef = useRef(0);
  const sendingRef = useRef(false);
  const faqKeyRef = useRef(0);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const status = conversation?.status;
  const isClosed = status === "closed";

  const faqCategories = useMemo(
    () => Array.from(new Set(faqs.map((f) => f.category).filter(Boolean))),
    [faqs]
  );

  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.seq));
      const merged = [...prev];
      for (const m of incoming) {
        if (!seen.has(m.seq)) merged.push(m);
      }
      merged.sort((a, b) => a.seq - b.seq);
      return merged;
    });
    lastSeqRef.current = incoming.reduce((mx, m) => Math.max(mx, m.seq), lastSeqRef.current);
  }, []);

  const refreshPast = useCallback(() => {
    axios
      .get(`${API}/customer/chat/conversations`)
      .then((res) => setPastConvs((res.data || []).filter((c) => c.status === "closed")))
      .catch(() => {});
  }, []);

  // Initial load: find an existing open conversation (if any) + past conversations.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/customer/chat/conversation`);
        if (!active) return;
        if (res.data) {
          setConversation(res.data);
          const msgRes = await axios.get(`${API}/customer/chat/${res.data.id}/messages?after_seq=0`);
          if (!active) return;
          setConversation(msgRes.data.conversation);
          mergeMessages(msgRes.data.messages);
        }
      } catch (e) {
        console.error("Failed to load chat:", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    refreshPast();
    return () => {
      active = false;
    };
  }, [mergeMessages, refreshPast]);

  // Poll for new messages + status changes while a conversation exists.
  useEffect(() => {
    if (!conversation?.id || isClosed) return;
    const convId = conversation.id;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/customer/chat/${convId}/messages?after_seq=${lastSeqRef.current}`);
        setConversation(res.data.conversation);
        mergeMessages(res.data.messages);
      } catch (e) {
        // transient
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [conversation?.id, isClosed, mergeMessages]);

  // When a chat closes, surface it in the past list.
  useEffect(() => {
    if (isClosed) refreshPast();
  }, [isClosed, refreshPast]);

  useEffect(() => {
    axios.get(`${API}/faqs`).then((res) => setFaqs(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, faqThread]);

  const handleFaqClick = (faq) => {
    faqKeyRef.current += 1;
    setFaqThread((prev) => [...prev, { key: faqKeyRef.current, question: faq.question, answer: faq.answer }]);
  };

  const startConversation = async () => {
    const body = draft.trim();
    if (!body || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await axios.post(`${API}/customer/chat/conversation`, { message: body });
      setConversation(res.data);
      setDraft("");
      const msgRes = await axios.get(`${API}/customer/chat/${res.data.id}/messages?after_seq=0`);
      setConversation(msgRes.data.conversation);
      mergeMessages(msgRes.data.messages);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start chat");
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  const sendMessage = async () => {
    const body = draft.trim();
    if (!body || !conversation?.id || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    try {
      const res = await axios.post(`${API}/customer/chat/${conversation.id}/messages`, { body });
      setDraft("");
      mergeMessages([res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
      sendingRef.current = false;
    }
  };

  const onSubmit = conversation ? sendMessage : startConversation;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("File is larger than 5MB.");
      return;
    }
    uploadFile(f);
  };

  const uploadFile = async (f) => {
    if (uploading) return;
    setUploading(true);
    try {
      // Attaching a file can also START the chat if none is open yet.
      let convId = conversation?.id;
      if (!convId) {
        const firstMsg = draft.trim() || "I've attached a file.";
        const started = await axios.post(`${API}/customer/chat/conversation`, { message: firstMsg });
        convId = started.data.id;
        setConversation(started.data);
        setDraft("");
        const m = await axios.get(`${API}/customer/chat/${convId}/messages?after_seq=0`);
        setConversation(m.data.conversation);
        mergeMessages(m.data.messages);
      }
      const form = new FormData();
      form.append("file", f);
      const res = await axios.post(`${API}/customer/chat/${convId}/upload`, form);
      mergeMessages([res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload file");
    } finally {
      setUploading(false);
    }
  };

  const openPast = async (conv) => {
    setViewConv(conv);
    setViewMessages([]);
    setViewLoading(true);
    try {
      const res = await axios.get(`${API}/customer/chat/${conv.id}/messages?after_seq=0`);
      setViewMessages(res.data.messages || []);
    } catch (e) {
      toast.error("Could not load that conversation");
    } finally {
      setViewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f4cc1e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare size={24} className="text-[#f4cc1e]" />
        <h1 className="text-2xl font-bold text-white font-['Chivo']">Support</h1>
      </div>

      <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg flex flex-col h-[calc(100vh-260px)] min-h-[420px]">
        <CardHeader className="border-b border-[#1a3a6a] py-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <span>RoofNuts Support</span>
            {status === "waiting" && <span className="text-xs font-normal text-[#f4cc1e]">· Waiting for an agent…</span>}
            {status === "active" && (
              <span className="text-xs font-normal text-green-400">
                · {conversation?.assigned_admin_name || "An agent"} is here
              </span>
            )}
            {isClosed && <span className="text-xs font-normal text-white/50">· Closed — transcript emailed</span>}
          </CardTitle>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 space-y-3">
          {!conversation && faqThread.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center text-white/50">
              <MessageSquare size={48} className="mb-4 opacity-50" />
              <p>Start a conversation with our team</p>
              <p className="text-sm mt-1">Type your message below, or tap a quick-help topic to get an instant answer.</p>
            </div>
          )}

          {conversation && messages.map((m) => <CustomerMessage key={m.seq} m={m} />)}

          {/* FAQ self-service Q&A (client-side only) */}
          {faqThread.map((item) => (
            <div key={`faq-${item.key}`} className="space-y-2">
              <div className="flex justify-end">
                <div className="max-w-[78%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words bg-[#f4cc1e] text-[#091c3c]">
                  {item.question}
                </div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[85%] flex flex-col items-start">
                  <div className="px-3 py-2 rounded-lg text-sm whitespace-pre-wrap break-words bg-[#0d2a5a] text-white border border-[#1a3a6a]">
                    {item.answer}
                  </div>
                  <span className="text-[10px] text-white/40 mt-1 px-1 flex items-center gap-1">
                    <HelpCircle size={10} /> RoofNuts FAQ
                  </span>
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </CardContent>

        {/* FAQ suggested pills */}
        {!isClosed && faqs.length > 0 && (
          <div className="border-t border-[#1a3a6a] px-3 pt-2.5 flex flex-wrap gap-2">
            {!faqCategory ? (
              <>
                <span className="text-[11px] text-white/40 self-center mr-1">Quick help:</span>
                {faqCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setFaqCategory(cat)}
                    className="text-xs rounded-full px-3 py-1 bg-[#0d2a5a] border border-[#1a3a6a] text-white/80 hover:border-[#f4cc1e]/50 hover:text-[#f4cc1e] transition-colors"
                  >
                    {cat}
                  </button>
                ))}
              </>
            ) : (
              <>
                <button
                  onClick={() => setFaqCategory(null)}
                  className="text-xs rounded-full px-2.5 py-1 bg-[#0d2a5a] border border-[#1a3a6a] text-white/60 hover:text-[#f4cc1e] transition-colors flex items-center gap-1"
                >
                  <ChevronLeft size={12} /> Topics
                </button>
                {faqs
                  .filter((f) => f.category === faqCategory)
                  .map((f) => (
                    <button
                      key={f.id}
                      onClick={() => handleFaqClick(f)}
                      className="text-xs rounded-full px-3 py-1 bg-[#0d2a5a] border border-[#1a3a6a] text-white/80 hover:border-[#f4cc1e]/50 hover:text-[#f4cc1e] transition-colors"
                    >
                      {f.question}
                    </button>
                  ))}
              </>
            )}
          </div>
        )}

        <div className="border-t border-[#1a3a6a] p-3">
          {isClosed ? (
            <p className="text-center text-sm text-white/50 py-2">
              This conversation has been closed. A transcript was emailed to you. Start a new chat anytime.
            </p>
          ) : (
            <div className="flex gap-2 items-end">
              <input ref={fileInputRef} type="file" hidden onChange={onPickFile} />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Attach a photo or file (max 5MB)"
                variant="outline"
                className="border-[#1a3a6a] text-white hover:bg-[#0d2a5a] h-10 w-10 p-0 shrink-0"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
              </Button>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sending}
                placeholder="Type your message…"
                rows={2}
                className="resize-none bg-[#0d2a5a] border-[#1a3a6a] text-white placeholder:text-white/40"
              />
              <Button
                onClick={onSubmit}
                disabled={sending || !draft.trim()}
                className="bg-[#f4cc1e] text-[#091c3c] hover:bg-[#f4cc1e]/90 h-10 px-4"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Past conversations */}
      {pastConvs.length > 0 && (
        <Card className="bg-[#091c3c] border-[#091c3c] rounded-xl shadow-lg">
          <CardHeader className="border-b border-[#1a3a6a] py-3">
            <CardTitle className="text-white text-sm flex items-center gap-2">
              <History size={16} className="text-[#f4cc1e]" /> Past conversations
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {pastConvs.map((c) => (
              <button
                key={c.id}
                onClick={() => openPast(c)}
                className="w-full text-left px-4 py-3 border-b border-[#1a3a6a] last:border-b-0 hover:bg-[#0d2a5a] transition-colors flex items-center justify-between gap-2"
              >
                <span className="text-sm text-white">Support chat</span>
                <span className="text-xs text-white/40">{formatTime(c.closed_at || c.last_message_at)}</span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Read-only viewer for a past conversation */}
      <Dialog open={!!viewConv} onOpenChange={(o) => { if (!o) setViewConv(null); }}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white text-base">
              Support chat · {viewConv ? formatTime(viewConv.closed_at || viewConv.last_message_at) : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
            {viewLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#f4cc1e]"></div>
              </div>
            ) : (
              viewMessages.map((m) => <CustomerMessage key={m.seq} m={m} />)
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerChat;
